import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pbEngineer } from '../lib/pb';
import { useData } from '../data/DataProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';
import { fetchTodayWeather } from './weather';

// Tenant base for the weather strip — falls back to the Guildford seed area
// (tenants don't carry lat/lng today). The strip hides gracefully offline.
const DEFAULT_BASE = { lat: 51.2362, lng: -0.5704 };

export default function EngineerDashboard() {
  const { data } = useData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [sheetsByJob, setSheetsByJob] = useState({});
  const [weather, setWeather] = useState(null);
  const [stats, setStats] = useState({
    active: 0,
    completed: 0,
    assigned: 0,
    pending: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const engineerId = pbEngineer.authStore.record?.id;
      if (!engineerId) return;

      const jobsList = await pbEngineer.collection('engineer_jobs').getFullList({
        filter: `engineer_id="${engineerId}"`,
        sort: '-created',
        expand: 'client_id',
      });

      setJobs(jobsList);

      // Map each job → its sheet (for the Recent Jobs detail link).
      pbEngineer.collection('engineer_job_sheets').getFullList({
        filter: `engineer_job_id.engineer_id="${engineerId}"`,
      })
        .then((sheets) => {
          const map = {};
          for (const s of sheets) map[s.engineer_job_id] = s.id;
          setSheetsByJob(map);
        })
        .catch(() => {});

      const stats = {
        active: jobsList.filter((j) => j.status === 'in_progress' || j.status === 'on_site').length,
        completed: jobsList.filter((j) => j.status === 'completed').length,
        assigned: jobsList.filter((j) => j.status === 'assigned').length,
        pending: jobsList.filter((j) => j.status === 'en_route').length,
      };
      setStats(stats);

      // Weather strip (spec §2.1) — cached 30 min, hidden when offline.
      const base = data.tenant?.lat && data.tenant?.lng
        ? { lat: data.tenant.lat, lng: data.tenant.lng }
        : DEFAULT_BASE;
      const w = await fetchTodayWeather(base.lat, base.lng);
      setWeather(w);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => ({
    assigned: 'blue',
    en_route: 'yellow',
    on_site: 'orange',
    in_progress: 'purple',
    completed: 'green',
    cancelled: 'red',
  }[status] || 'gray');

  const openJob = (job) => {
    // Prefer the job sheet detail when a sheet exists; otherwise the job
    // sheets list (no sheet yet → create one from there).
    if (sheetsByJob[job.id]) {
      navigate(`jobs/${sheetsByJob[job.id]}`);
    } else {
      navigate('jobs');
    }
  };

  // Today's run order: jobs scheduled today, ordered by scheduled time then
  // a simple nearest-neighbour pass on coordinates (spec §2.1).
  const todayStr = new Date().toISOString().slice(0, 10);
  const todaysJobs = jobs
    .filter((job) => job.status !== 'completed' && job.status !== 'cancelled')
    .filter((job) => (job.scheduled_at || '').slice(0, 10) === todayStr)
    .sort((a, b) => (a.scheduled_at || '').localeCompare(b.scheduled_at || ''));

  const orderedRun = (() => {
    const remaining = [...todaysJobs];
    const ordered = [];
    let cursor = null;
    const dist = (a, b) => {
      const ca = a.coordinates, cb = b.coordinates;
      if (!ca || !cb) return 0;
      return Math.abs(ca.lat - cb.lat) + Math.abs(ca.lng - cb.lng);
    };
    while (remaining.length > 0) {
      let idx = 0;
      if (cursor) {
        let best = Infinity;
        remaining.forEach((job, i) => {
          const d = dist(cursor, job);
          if (d < best) { best = d; idx = i; }
        });
      }
      const next = remaining.splice(idx, 1)[0];
      ordered.push(next);
      cursor = next;
    }
    return ordered;
  })();

  const fmtTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isUrgent = (job) => job.priority === 'urgent';

  if (loading) {
    return <div className="page-container"><Skeleton rows={8} /></div>;
  }

  return (
    <div className="page-container engineer-dashboard">
      <div className="page-header">
        <h1>Engineer Dashboard</h1>
        <p>Welcome back, {pbEngineer.authStore.record?.name || 'Engineer'}</p>
      </div>

      {weather && (
        <div className="weather-strip">
          <SafeIcon icon={Icons.Cloud} />
          <strong>{weather.label}</strong>
          <span>{weather.tMin}° / {weather.tMax}°</span>
          <span className="weather-muted">
            {weather.precip > 0 ? `${weather.precip} mm rain today` : 'No rain expected'}
          </span>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card active">
          <SafeIcon icon={Icons.Activity} />
          <div>
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Active Jobs</span>
          </div>
        </div>
        <div className="stat-card completed">
          <SafeIcon icon={Icons.CheckCircle} />
          <div>
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
        <div className="stat-card assigned">
          <SafeIcon icon={Icons.Clock} />
          <div>
            <span className="stat-value">{stats.assigned}</span>
            <span className="stat-label">Assigned</span>
          </div>
        </div>
        <div className="stat-card pending">
          <SafeIcon icon={Icons.Navigation} />
          <div>
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">En Route</span>
          </div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="section recent-jobs">
          <div className="section-header">
            <h2>Recent Jobs</h2>
            <button className="text-button" onClick={() => navigate('jobs')}>View All</button>
          </div>

          {jobs.length === 0 ? (
            <EmptyState
              title="No jobs assigned"
              message="You don't have any jobs assigned yet"
              icon={Icons.Wrench}
            />
          ) : (
            <div className="job-list">
              {jobs.slice(0, 6).map(job => (
                <div key={job.id} className={`job-card ${isUrgent(job) ? 'urgent' : ''}`}>
                  <div className="job-header">
                    <div className="job-header-left" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`job-status status-${getStatusColor(job.status)}`}>
                        {job.status.replace('_', ' ')}
                      </div>
                      {isUrgent(job) && (
                        <span className="priority-chip urgent">
                          <SafeIcon icon={Icons.AlertTriangle} size={10} /> Urgent
                        </span>
                      )}
                    </div>
                    <span className="job-time">
                      {new Date(job.created).toLocaleDateString()}
                    </span>
                  </div>
                  <h3>{job.title}</h3>
                  <p className="job-client">
                    {job.expand?.client_id?.name || 'Unknown Client'}
                  </p>
                  <p className="job-address">{job.address}</p>
                  {job.estimated_duration_minutes && (
                    <div className="job-eta">
                      <SafeIcon icon={Icons.Clock} />
                      Est. {job.estimated_duration_minutes} min
                    </div>
                  )}
                  <button className="primary-button" onClick={() => openJob(job)}>
                    {sheetsByJob[job.id] ? 'Open Job Sheet' : 'View Job'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-column">
          {orderedRun.length > 0 && (
            <div className="section run-order">
              <div className="section-header">
                <h2>Today's Run</h2>
                <SafeIcon icon={Icons.Navigation} size={14} />
              </div>
              <div className="run-order-list">
                {orderedRun.map((job) => (
                  <button
                    key={job.id}
                    className={`run-order-item ${isUrgent(job) ? 'urgent' : ''}`}
                    onClick={() => openJob(job)}
                  >
                    <span className="run-order-time">{fmtTime(job.scheduled_at)}</span>
                    <div className="run-order-main">
                      <h4>{job.title}</h4>
                      <p>{job.expand?.client_id?.name || job.address || ''}</p>
                    </div>
                    {isUrgent(job) && <span className="priority-chip urgent">URGENT</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="section quick-actions">
            <div className="section-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="action-grid">
              <button className="action-card" onClick={() => navigate('jobs/new')}>
                <SafeIcon icon={Icons.Plus} />
                <span>New Job Sheet</span>
              </button>
              <button className="action-card" onClick={() => navigate('certificates')}>
                <SafeIcon icon={Icons.FileText} />
                <span>New Certificate</span>
              </button>
              <button className="action-card" onClick={() => navigate('mapping')}>
                <SafeIcon icon={Icons.Map} />
                <span>View Map</span>
              </button>
              <button className="action-card" onClick={() => navigate('messages')}>
                <SafeIcon icon={Icons.MessageCircle} />
                <span>Messages</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
